import React, { useState, useEffect, useRef } from 'react';
import api from './axiosConfig';
import { Box, Typography, Button, Alert, Paper, CircularProgress } from '@mui/material';

// Salon logo, printed at the top of generated documents. Shown on Settings > Documents.
// Everyone on the account can see it; only the account owner gets the buttons.
export default function LogoUpload() {
  const [hasLogo, setHasLogo] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);      // { severity: 'success' | 'warning' | 'error', text }
  const fileInput = useRef(null);
  const urlRef = useRef(null);

  const setPreview = (blob) => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = blob ? URL.createObjectURL(blob) : null;
    setPreviewUrl(urlRef.current);
  };

  const refresh = async () => {
    try {
      const status = await api.get('/documents/logo');
      setHasLogo(!!status.data.has_logo);
      setCanEdit(!!status.data.can_edit);
      if (status.data.has_logo) {
        const img = await api.get('/documents/logo/image', { responseType: 'blob' });
        setPreview(img.data);
      } else {
        setPreview(null);
      }
    } catch (e) {
      console.error('Could not load logo:', e);
    }
  };

  useEffect(() => {
    refresh();
    return () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); };
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';                              // lets the same file be chosen again
    if (!file) return;
    setBusy(true);
    setMessage(null);
    try {
      const form = new FormData();
      form.append('logo', file);
      const res = await api.post('/documents/logo', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const warnings = res.data.warnings || [];
      setMessage(warnings.length
        ? { severity: 'warning', text: 'Logo saved. ' + warnings.join(' ') }
        : { severity: 'success', text: 'Logo saved. It will appear on documents you generate from now on.' });
      await refresh();
    } catch (err) {
      setMessage({ severity: 'error', text: err.response?.data?.error || 'Could not upload the logo. Please try again.' });
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove your logo? Documents will be produced without one.')) return;
    setBusy(true);
    setMessage(null);
    try {
      await api.delete('/documents/logo');
      setMessage({ severity: 'success', text: 'Logo removed.' });
      await refresh();
    } catch (err) {
      setMessage({ severity: 'error', text: err.response?.data?.error || 'Could not remove the logo. Please try again.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
      <Typography variant="subtitle1" gutterBottom>Your logo</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Printed at the top of your documents. Use a PNG or JPG at least 200 pixels wide and under 5 MB.
        A logo on a transparent or plain white background works best. It is resized to fit the space and
        never stretched.
      </Typography>

      <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50', p: 2,
                 minHeight: 96, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
        {previewUrl ? (
          <Box component="img" src={previewUrl} alt="Your logo"
               sx={{ maxWidth: '100%', maxHeight: 120, bgcolor: 'white' }} />
        ) : hasLogo ? (
          <CircularProgress size={24} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No logo uploaded yet. Documents will be produced without one.
          </Typography>
        )}
      </Box>

      {canEdit ? (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <input ref={fileInput} type="file" accept="image/png,image/jpeg" onChange={handleFile} hidden />
          <Button variant="contained" disabled={busy} onClick={() => fileInput.current && fileInput.current.click()}>
            {busy ? 'Working...' : hasLogo ? 'Replace logo' : 'Upload logo'}
          </Button>
          {hasLogo && (
            <Button color="error" variant="outlined" disabled={busy} onClick={handleRemove}>Remove logo</Button>
          )}
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Only the account owner can change the logo.
        </Typography>
      )}

      {message && <Alert severity={message.severity} sx={{ mt: 2 }}>{message.text}</Alert>}
    </Paper>
  );
}